import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useScoringDetail } from '../hooks/useScoring.js';
import { ScoreGrade } from '../components/scoring/ScoreGrade';
import { AxisChart } from '../components/scoring/AxisChart';
import { RentalPlanTable } from '../components/scoring/RentalPlanTable';
import { RiskAssessment } from '../components/scoring/RiskAssessment';
import { MarketIntelligence } from '../components/scoring/MarketIntelligence';
import { Card } from '../components/ui/Card';
import { LoadingScreen } from '../components/feedback/LoadingScreen';

/**
 * @graph-node {Page}
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business スコアリング結果詳細ページ。IDを指定してスコアリング結果を全セクション表示
 * @graph-connects useScoringDetail [calls] スコアリング詳細フック呼び出し
 */
export const Route = createFileRoute('/result/$id')({
  component: ResultDetailPage,
});

/** @graph-connects none */
function ResultDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { data: result, isLoading, error } = useScoringDetail(id);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error || !result) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card variant="glass">
          <div className="risk-warning">
            <p className="text-sm">{t('scoring.notFound')}</p>
          </div>
        </Card>
        <Link to="/history" className="text-sm text-[var(--color-accent-primary)] hover:underline">
          {t('history.backToList')}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/history"
            className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent-primary)] transition-colors"
          >
            {t('history.backToList')}
          </Link>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mt-1">
            {result.productName}
          </h1>
          <p className="text-sm text-[var(--color-text-secondary)]">
            {new Date(result.timestamp).toLocaleString('ja-JP')}
          </p>
        </div>
      </div>

      {/* 結果表示 */}
      <div className="space-y-4 animate-fade-in-up">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ScoreGrade
            grade={result.grade}
            total={result.total}
            gradeColor={result.gradeColor}
            gradeBg={result.gradeBg}
          />
          <Card variant="glass">
            <div className="space-y-4">
              {result.strengths.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-[var(--color-success)] mb-2">
                    {t('scoring.strengths')}
                  </h4>
                  <ul className="space-y-1">
                    {result.strengths.map((s, i) => (
                      <li
                        key={i}
                        className="text-sm text-[var(--color-text-secondary)] flex items-start gap-2"
                      >
                        <span className="text-[var(--color-success)] mt-0.5">+</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.weaknesses.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-[var(--color-error)] mb-2">
                    {t('scoring.weaknesses')}
                  </h4>
                  <ul className="space-y-1">
                    {result.weaknesses.map((w, i) => (
                      <li
                        key={i}
                        className="text-sm text-[var(--color-text-secondary)] flex items-start gap-2"
                      >
                        <span className="text-[var(--color-error)] mt-0.5">-</span>
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.advice && (
                <div className="pt-2 border-t border-[var(--color-border)]">
                  <p className="text-sm text-[var(--color-text-secondary)] italic">
                    {result.advice}
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>

        <AxisChart axes={result.axes} />
        <RentalPlanTable plans={result.rentalPlans} />
        <RiskAssessment risks={result.risks} />
        <MarketIntelligence result={result} />
      </div>
    </div>
  );
}
